/**
 * Token Service — Persistent auth across app close/reopen.
 *
 * Uses localStorage so the JWT survives:
 *   - App close + reopen (PWA standalone, mobile browser)
 *   - Service-Worker reloads (the SW never touches /api/* — see public/service-worker.js)
 *
 * Backend issues a 30-day JWT (ACCESS_TOKEN_EXPIRE_MINUTES = 60*24*30); this
 * file just reads it and auto-clears the session if `exp` has passed.
 *
 * For users that previously had data in sessionStorage we transparently
 * migrate on first read so they don't get logged out by the upgrade.
 *
 * Keys:
 *   superhost_token     — JWT access token
 *   superhost_user      — serialized user object (non-sensitive profile data)
 *   superhost_property  — serialized selected property
 *   otp_session_id      — temporary OTP session
 *   otp_phone           — temporary OTP phone
 */

const KEYS = {
  TOKEN: 'superhost_token',
  USER: 'superhost_user',
  PROPERTY: 'superhost_property',
  OTP_SESSION: 'otp_session_id',
  OTP_PHONE: 'otp_phone',
  PROPERTY_ID: 'propertyId',
};

// One-time migration: copy any leftover sessionStorage values into localStorage
// so users coming from the previous build stay logged in.
(function migrateSessionToLocal() {
  try {
    Object.values(KEYS).forEach((key) => {
      const ss = sessionStorage.getItem(key);
      if (ss && !localStorage.getItem(key)) {
        localStorage.setItem(key, ss);
      }
      // Always drop the sessionStorage copy so we have one source of truth.
      sessionStorage.removeItem(key);
    });
  } catch {
    /* storage may be unavailable in private mode — ignore */
  }
})();

// ─── JWT helpers ───────────────────────────────────────────────

function decodeJwtPayload(token) {
  try {
    const base64 = token.split('.')[1];
    const json = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function isTokenExpired(token) {
  if (!token) return true;
  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) return false; // no exp claim — treat as valid
  return Date.now() >= payload.exp * 1000;
}

// ─── Token ─────────────────────────────────────────────────────

export function setToken(token) {
  localStorage.setItem(KEYS.TOKEN, token);
}

export function getToken() {
  const token = localStorage.getItem(KEYS.TOKEN);
  if (token && isTokenExpired(token)) {
    // Token expired — auto clear and bounce to login
    clearAll();
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    return null;
  }
  return token;
}

export function removeToken() {
  localStorage.removeItem(KEYS.TOKEN);
}

// ─── User Data ─────────────────────────────────────────────────

export function setUserData(data) {
  localStorage.setItem(KEYS.USER, JSON.stringify(data));
}

export function getUserData() {
  try {
    const raw = localStorage.getItem(KEYS.USER);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function removeUserData() {
  localStorage.removeItem(KEYS.USER);
}

// ─── Property ──────────────────────────────────────────────────

export function setPropertyData(data) {
  localStorage.setItem(KEYS.PROPERTY, JSON.stringify(data));
}

export function getPropertyData() {
  try {
    const raw = localStorage.getItem(KEYS.PROPERTY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function removePropertyData() {
  localStorage.removeItem(KEYS.PROPERTY);
}

// ─── OTP Session (temporary) ───────────────────────────────────

export function setOtpSession(sessionId, phone) {
  localStorage.setItem(KEYS.OTP_SESSION, sessionId);
  localStorage.setItem(KEYS.OTP_PHONE, phone);
}

export function getOtpSession() {
  return {
    sessionId: localStorage.getItem(KEYS.OTP_SESSION),
    phone: localStorage.getItem(KEYS.OTP_PHONE),
  };
}

export function clearOtpSession() {
  localStorage.removeItem(KEYS.OTP_SESSION);
  localStorage.removeItem(KEYS.OTP_PHONE);
}

// ─── Property ID (used by ProfileScreen) ───────────────────────

export function setPropertyId(id) {
  localStorage.setItem(KEYS.PROPERTY_ID, id);
}

// ─── Bulk clear ────────────────────────────────────────────────

export function clearAll() {
  Object.values(KEYS).forEach((key) => localStorage.removeItem(key));
}

// ─── Check authenticated ───────────────────────────────────────

export function hasValidToken() {
  const token = localStorage.getItem(KEYS.TOKEN);
  return !!token && !isTokenExpired(token);
}
